import { useContext, useMemo, useSyncExternalStore } from "react";
import {
  INSTALLABLE_MODULE_KEYS,
  moduleForWorkLinkRef,
  moduleUnavailablePhrase,
  type InstallableModuleKey,
  type ModuleAvailabilityStatus,
} from "@playbookai/types";
import { ApiClientContext } from "./context.js";

/** Status per installable module, as the shell computed it (ADR-0100). */
export type ModuleStatusMap = Readonly<
  Record<InstallableModuleKey, ModuleAvailabilityStatus>
>;

/**
 * The reactive availability seam the shell hands in, in the same
 * `{getSnapshot, subscribe}` shape as `authScope` and `tenantRoles`. The shell
 * derives every status from its one `isModuleAvailable` rule; a feature never
 * re-derives it, it only reads this.
 */
export interface ModuleAvailabilitySource {
  readonly getSnapshot: () => ModuleStatusMap;
  readonly subscribe: (listener: () => void) => () => void;
}

/**
 * What a person calls each module in the "turned off" sentence. Exhaustive by
 * type, so a new installable module fails to compile until it is named here.
 */
export const MODULE_DISPLAY_NAMES: Readonly<
  Record<InstallableModuleKey, string>
> = {
  solve: "SOLVE",
  vsm: "VSM",
  hoshin: "Hoshin",
  kpi: "KPI Manager",
  lama: "LAMA",
  knowledge_studio: "Knowledge Studio",
};

/**
 * What one linked item of each module is called in the hidden-links line.
 * Exhaustive by type, like {@link MODULE_DISPLAY_NAMES}.
 */
export const MODULE_ITEM_NOUNS: Readonly<
  Record<InstallableModuleKey, readonly [one: string, other: string]>
> = {
  solve: ["SOLVE", "SOLVEs"],
  vsm: ["value stream", "value streams"],
  hoshin: ["Hoshin item", "Hoshin items"],
  kpi: ["KPI", "KPIs"],
  lama: ["LAMA campaign", "LAMA campaigns"],
  knowledge_studio: ["guide", "guides"],
};

/**
 * One greyed line for old links a screen cannot name because their module is
 * off — the name lives in that module, which is not read (Vivek, 12 Oct 2026):
 * "2 linked SOLVEs — SOLVE is turned off for this workspace". `null` when the
 * module is available or nothing is hidden.
 */
export function hiddenLinksMessage(
  key: InstallableModuleKey,
  status: ModuleAvailabilityStatus,
  count: number,
): string | null {
  if (status === "available" || count <= 0) return null;
  const [one, other] = MODULE_ITEM_NOUNS[key];
  return `${count} linked ${count === 1 ? one : other} — ${moduleUnavailablePhrase(MODULE_DISPLAY_NAMES[key], status)}`;
}

/** The d7 sentence for an unavailable module; `null` when it is available. */
export function moduleUnavailableMessage(
  key: InstallableModuleKey,
  status: ModuleAvailabilityStatus,
): string | null {
  return status === "available"
    ? null
    : moduleUnavailablePhrase(MODULE_DISPLAY_NAMES[key], status);
}

/**
 * Without a seam (a test host, a mock config) every module reads as
 * available — exactly what every screen showed before ADR-0100, so a host that
 * has not wired availability changes nothing.
 */
export const ALL_MODULES_AVAILABLE: ModuleStatusMap = Object.freeze(
  Object.fromEntries(
    INSTALLABLE_MODULE_KEYS.map((key) => [key, "available"]),
  ) as Record<InstallableModuleKey, ModuleAvailabilityStatus>,
);

export interface ModuleAvailability {
  readonly status: (key: InstallableModuleKey) => ModuleAvailabilityStatus;
  readonly isAvailable: (key: InstallableModuleKey) => boolean;
  /** The d7 sentence, or `null` when the module is available. */
  readonly unavailableMessage: (key: InstallableModuleKey) => string | null;
  /**
   * For a work link's `refName`: the sentence when the module that owns that
   * kind is unavailable, else `null` (including `external_ref`, which belongs
   * to no module, and kinds this build does not know).
   */
  readonly linkUnavailableMessage: (refName: string) => string | null;
  /** {@link hiddenLinksMessage} for this module's current status. */
  readonly hiddenLinksMessage: (
    key: InstallableModuleKey,
    count: number,
  ) => string | null;
}

const noSubscribe = (): (() => void) => () => undefined;
const allAvailable = (): ModuleStatusMap => ALL_MODULES_AVAILABLE;

/**
 * Read module availability in a feature. Re-renders when the shell publishes
 * a new list (a session refresh, a `module_unavailable` refusal). Works without
 * an `ApiClientProvider`, so a data-light component can ask without making its
 * tests mount one.
 */
export function useModuleAvailability(): ModuleAvailability {
  const source = useContext(ApiClientContext)?.moduleAvailability;
  const map = useSyncExternalStore(
    source?.subscribe ?? noSubscribe,
    source?.getSnapshot ?? allAvailable,
  );
  return useMemo(() => moduleAvailabilityFrom(map), [map]);
}

/** The same answers from a plain map, for code outside React. */
export function moduleAvailabilityFrom(
  map: ModuleStatusMap,
): ModuleAvailability {
  const status = (key: InstallableModuleKey): ModuleAvailabilityStatus =>
    map[key];
  const unavailableMessage = (key: InstallableModuleKey): string | null =>
    moduleUnavailableMessage(key, map[key]);
  return {
    status,
    isAvailable: (key) => map[key] === "available",
    unavailableMessage,
    linkUnavailableMessage: (refName) => {
      const owner = moduleForWorkLinkRef(refName);
      return owner === null ? null : unavailableMessage(owner);
    },
    hiddenLinksMessage: (key, count) =>
      hiddenLinksMessage(key, map[key], count),
  };
}
